import moment from 'moment';
import { result } from 'lodash/object';
import { titleCase } from 'change-case';

import { generateShipmentPDFReceipt } from 'utils/receipt_pdf_generator';
import { trimMoney, truncate } from 'utils/strings';

import Shipments from 'app/collections/shipments';
import DateRange, { DATERANGE_PARAM_TYPES } from 'app/models/date_range';
import ShipmentReport from 'app/models/shipment_report';

import Base from 'app/views/base';
import DatepickerRangeView from 'app/views/datepicker_range_view';
import RequestReportView from 'app/views/request_report_view';
import ShipmentRefundModal from 'app/views/modals/shipment_refund_modal';
import SelectorView from 'app/views/selector_view';

import { List as ListView, Heading } from 'app/components/list/list';
import TrackingCodeHeadingView from 'app/components/list/item/headings/tracking_code';
import DateAndTimeHeadingView from 'app/components/list/item/headings/date_and_time';
import { DetailBlock } from 'app/components/list/object_details';
import ListSearchView from 'app/components/list/search';
import PageHeaderView from 'app/components/page_header';

import tmpl from './template';
import parcelDetailTemplate from './parcel_detail';
import postageLabelButtonTemplate from './postage_label_button';
import receiptButtonTemplate from './receipt_button';
import refundButtonTemplate from './refund_button';

export const ParcelDetailView = Base.extend({
  compiledTemplate: parcelDetailTemplate,

  context() {
    const parcel = this.model.get('parcel');
    return {
      dimensions: result(parcel, 'dimensions'),
      predefinedPackage: parcel ? parcel.get('predefined_package') : '',
      weight: result(parcel, 'weight')
    };
  },
});

export const PostageLabelButtonView = Base.extend({
  compiledTemplate: postageLabelButtonTemplate,

  context() {
    return { postageLabelUrl: result(this.model.get('postage_label'), 'imageUrl') };
  },
});

export const ReceiptButtonView = Base.extend({
  compiledTemplate: receiptButtonTemplate,

  initialize({ user }) {
    this.user = user;
  },

  events: { 'click .receipt': 'generateReceipt' },

  context() {
    const fees = this.model.get('fees');
    return { hasFees: Array.isArray(fees) && !!fees.length };
  },

  generateReceipt(event) {
    event.preventDefault();
    const createdDate = moment(this.model.get('created_at') || '').utc();
    const fees = this.model.get('fees');

    if (!createdDate.isValid()) throw new Error('created_at property not present');
    if (!fees.length) throw new Error('no fees on this shipment');

    const filename = `easypost_${createdDate.format('YYYYMMDDHHmmss')}_${this.model.id}`;

    generateShipmentPDFReceipt(filename, {
      account: this.user.get('email'),
      name: this.user.get('name'),
      date: createdDate.format('YYYY-MM-DD'),
      time: createdDate.format('HH:mm:ss'),
      timezone: 'UTC',
      id: this.model.id,
      fees,
    });
  },
});

export const RefundButtonView = Base.extend({
  compiledTemplate: refundButtonTemplate,

  events: { 'click .refund': 'showRefundModal' },

  initialize() {
    this.refundModal = new ShipmentRefundModal({ model: this.model });
  },

  context() {
    return {
      refundStatus: titleCase(this.model.get('refund_status')),
      purchased: !!this.model.get('tracking_code'),
    };
  },

  showRefundModal(event) {
    event.preventDefault();
    this.refundModal.show();
  },

});

export const buildHeadings = () => {
  return [
    new Heading({
      subView: m => new DateAndTimeHeadingView({ model: m }),
      display: 'Date',
      xsSize: 3,
      smSize: 2,
      wrapperClass: 'text-nowrap',
    }),
    new Heading({
      text: m => truncate(result(m.get('to_address'), 'nameAndCompany'), 25),
      display: 'To',
      xsSize: 5,
      smSize: 3,
      wrapperClass: 'overflow-ellipsis'
    }),
    new Heading({
      subView: m => new TrackingCodeHeadingView({ model: m.get('tracker') }),
      display: 'Tracking Code',
      xsSize: 4,
      smSize: 4,
    }),
    new Heading({
      text: m => {
        const currencyCode = result(m.get('selected_rate'), 'currency');
        const rate = result(m.get('selected_rate'), 'rate');
        return rate ? trimMoney(rate, currencyCode) : '';
      },
      display: 'Cost',
      smSize: 1,
      xsHidden: true
    }),
  ];
};

export const buildShowDetails = (user) => {
  return {
    mainHeading: m => m.get('id'),
    detailBlocks: [
      new DetailBlock({
        heading: 'From',
        items: m => {
          const from =  m.get('from_address');
          if (!from) return [];
          return [
            result(from, 'nameAndCompany'),
            `${from.get('street1') || ''} ${from.get('street2') || ''}`,
            result(from, 'cityStateZipCountry'),
          ];
        }
      }),
      new DetailBlock({
        heading: 'To',
        items: m => {
          const to =  m.get('to_address');
          if (!to) return [];
          return [
            result(to, 'nameAndCompany'),
            `${to.get('street1') || ''} ${to.get('street2') || ''}`,
            result(to, 'cityStateZipCountry'),
          ];
        }
      }),
      new DetailBlock({
        heading: 'Parcel',
        subViews: [ m => new ParcelDetailView({ model: m }) ],
      }),
      new DetailBlock({
        heading: '',
        wrapperSubClass: 'buttons',
        subViews: [
          m => new PostageLabelButtonView({ model: m }),
          m => new ReceiptButtonView({ model: m, user }),
          m => new RefundButtonView({ model: m }),
        ],
      }),
    ],
  };
};

export default Base.extend({
  compiledTemplate: tmpl,
  className: 'shipments',

  initialize({ apiModeSelection, user }) {
    this.user = user;
    this.apiModeSelection = apiModeSelection;

    this.dateRange = new DateRange({ paramType: DATERANGE_PARAM_TYPES.DATETIME });
    this.collection = new Shipments([], { apiModeSelection: this.apiModeSelection, dateRange: this.dateRange });

    this.listenTo(this.collection, 'update', this.render);

    this.addSubview('header', 'header', new PageHeaderView({
      name: 'Shipments', links: [
        { name: 'Docs', href: '/docs/api#shipments' },
        { name: 'Guide', href: '/getting-started' },
      ],
    }));
    this.addSubview('apiModeSelector', '#api_mode_selector', new SelectorView({ model: this.apiModeSelection }));
    this.addSubview('datepickers', '#shipment_daterange_pickers', new DatepickerRangeView({ model: this.dateRange }));
    this.addSubview('requestShipmentReport', '#request_shipment_report', new RequestReportView({
      collection: this.collection, ReportClass: ShipmentReport, dateRange: this.dateRange,
      apiModeSelection: this.apiModeSelection,
    }));
    this.addSubview('search', '#shipment_search', new ListSearchView({
      collection: this.collection, placeholder: '(Tracking Code, ID, or Reference)'
    }));
    this.addSubview('shipmentList', '#shipment_list', new ListView({
      headings: buildHeadings(),
      collection: this.collection,
      noResultsMessage: 'No postage has been purchased matching your filter set.',
      showDetails: buildShowDetails(user),
    }));
  },
});



// WEBPACK FOOTER //
// ./app/components/pages/shipments/index.js